import { Fragment } from 'react';
import clsx from 'clsx';
import { ArrowDownIcon, ArrowUpIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { AnalysisSubHeader } from '../../../../components';
import {
  FUNNEL_STAGE_ORDER,
  dropoff,
  type FunnelMatrix as FunnelMatrixData,
  type FunnelStageValues,
} from '../../model/funnel';
import { formatPercent } from '../../model/format';

interface FunnelMatrixProps {
  funnel: FunnelMatrixData;
}

interface FunnelColumn {
  key: string;
  label: string;
  isTarget: boolean;
  isCompetitorAverage: boolean;
  values: FunnelStageValues;
}

const StageCell = ({
  value,
  highlight,
  roundBottom,
}: {
  value: number | null;
  highlight: boolean;
  roundBottom: boolean;
}) => (
  <div
    className={clsx(
      'p-2',
      highlight && 'bg-surface',
      highlight && roundBottom && 'rounded-b-[10px]',
    )}
  >
    {value === null ? (
      <span className="block text-center text-sm text-muted-foreground">
        {formatPercent(value)}
      </span>
    ) : (
      <div className="relative h-[30px] overflow-hidden rounded-full border border-border-light bg-surface">
        <div
          className="absolute inset-y-0 left-0 bg-linear-to-r from-purple-200 to-purple-300 dark:from-violet-800 dark:to-violet-600"
          style={{ width: `${value}%` }}
        />
        <span className="absolute right-[10px] top-1/2 -translate-y-1/2 text-xs font-bold text-foreground">
          {formatPercent(value)}
        </span>
      </div>
    )}
  </div>
);

const DropoffCell = ({ drop, highlight }: { drop: number | null; highlight: boolean }) => {
  // A negative drop-off is a gain (the later stage exceeds the earlier one): point
  // the arrow up and show the magnitude, not a minus sign next to an up arrow.
  const isGain = drop !== null && drop < 0;
  const Arrow = isGain ? ArrowUpIcon : ArrowDownIcon;

  return (
    <div className={clsx('flex justify-center px-2 py-1.5', highlight && 'bg-surface')}>
      {drop === null ? (
        <span className="text-sm text-muted-foreground">{formatPercent(drop)}</span>
      ) : (
        <span className="inline-flex items-center gap-[3px] rounded-full bg-surface-raised px-[9px] py-[3px] text-[11px] font-medium text-foreground-alt">
          <Arrow className="size-[11px]" />
          {formatPercent(Math.abs(drop))}
        </span>
      )}
    </div>
  );
};

export const FunnelMatrix = ({ funnel }: FunnelMatrixProps) => {
  const { t } = useTranslation('analysis');

  const hasCompetitorData = Object.values(funnel.competitorAverage).some((value) => value !== null);

  const columns: FunnelColumn[] = funnel.brands.map((brand) => ({
    key: brand,
    label: brand,
    isTarget: brand === funnel.targetBrand,
    isCompetitorAverage: false,
    values: funnel.values[brand],
  }));

  if (hasCompetitorData) {
    columns.push({
      key: 'competitorAverage',
      label: t('analysisResult.marketingPerformance.competitorsAverage'),
      isTarget: false,
      isCompetitorAverage: true,
      values: funnel.competitorAverage,
    });
  }

  const gridTemplateColumns = `150px repeat(${columns.length}, 1fr)`;

  return (
    <div className="flex flex-col gap-1.5">
      <AnalysisSubHeader
        title={t('analysisResult.marketingPerformance.funnelStagePerformance')}
        subtitle={t('analysisResult.marketingPerformance.funnelStageSubtitle')}
        subtitleTone="meta"
      />
      <div className="overflow-x-auto rounded-xl border border-border-light p-4">
        <div className="flex min-w-[720px] flex-col">
          <div className="grid items-end" style={{ gridTemplateColumns, columnGap: '10px' }}>
            <div />
            {columns.map((column) => (
              <div
                key={column.key}
                className={clsx(
                  'px-1.5 py-2 text-center text-xs font-medium leading-[15px]',
                  column.isTarget
                    ? 'rounded-t-[10px] bg-surface text-foreground-alt'
                    : 'text-muted-foreground',
                )}
              >
                {column.label}
              </div>
            ))}
          </div>

          {FUNNEL_STAGE_ORDER.map((stage, index) => {
            const isLastStage = index === FUNNEL_STAGE_ORDER.length - 1;
            return (
              <Fragment key={stage}>
                <div
                  className="grid items-center"
                  style={{ gridTemplateColumns, columnGap: '10px' }}
                >
                  <div className="flex items-center text-sm font-medium text-foreground-alt">
                    {t(`analysisResult.marketingPerformance.${stage}`)}
                  </div>
                  {columns.map((column) => (
                    <StageCell
                      key={column.key}
                      value={column.values[stage]}
                      highlight={column.isTarget}
                      roundBottom={column.isTarget && isLastStage}
                    />
                  ))}
                </div>

                {!isLastStage && (
                  <div
                    className="grid items-center"
                    style={{ gridTemplateColumns, columnGap: '10px' }}
                  >
                    <div />
                    {columns.map((column) => (
                      <DropoffCell
                        key={column.key}
                        drop={
                          column.isCompetitorAverage
                            ? funnel.competitorAverageDropoffs[index]
                            : dropoff(
                                column.values[stage],
                                column.values[FUNNEL_STAGE_ORDER[index + 1]],
                              )
                        }
                        highlight={column.isTarget}
                      />
                    ))}
                  </div>
                )}
              </Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
};
